// Ayuda integrada · botón «?» (lo único que va en el bundle inicial).
//
// Muy discreto: pequeño, semitransparente, en la esquina inferior derecha y
// POR DEBAJO de cajones y diálogos (zIndex 1050 < drawer 1200 < modal 1300),
// así que nunca tapa la ventana de cobro ni los menús. En móvil sube por
// encima de la barra inferior del TPV. El aviso «¿Necesitas ayuda?» se
// muestra solo la primera vez (localStorage, con try/catch). El panel se
// carga bajo demanda al pulsar (React.lazy): no pesa en el arranque.

import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { ButtonBase, Tooltip } from "@mui/material";

const HelpPanel = lazy(() => import(/* webpackChunkName: "help-panel" */ "./HelpPanel"));

export const INTRO_KEY = "tmo:help:intro-seen";

// Nivel del botón y de su aviso: por debajo de cajones (1200) y diálogos (1300).
export const LAUNCHER_Z_INDEX = 1050;

function introSeen() {
  try {
    return window.localStorage.getItem(INTRO_KEY) === "1";
  } catch {
    return false;
  }
}

function markIntroSeen() {
  try {
    window.localStorage.setItem(INTRO_KEY, "1");
  } catch {
    /* sin almacenamiento: no se vuelve a mostrar en esta sesión */
  }
}

let introShownThisSession = false;

export default function HelpLauncher() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [intro, setIntro] = useState(false);

  useEffect(() => {
    if (introSeen() || introShownThisSession) return undefined;
    // El flag se marca al MOSTRARLO: al entrar en el TPV el layout se monta
    // dos veces y el primer montaje se desmonta antes de que salga el aviso.
    const show = setTimeout(() => {
      introShownThisSession = true;
      setIntro(true);
    }, 1500);
    const hide = setTimeout(() => {
      setIntro(false);
      markIntroSeen();
    }, 9500);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, []);

  const openPanel = useCallback(() => {
    setIntro(false);
    markIntroSeen();
    setMounted(true);
    setOpen(true);
  }, []);

  useEffect(() => {
    // Atajo: F1 abre la ayuda.
    const onKey = (e) => {
      if (e.key === "F1") {
        e.preventDefault();
        openPanel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openPanel]);

  return (
    <>
      <Tooltip
        title="¿Necesitas ayuda? Pregunta aquí"
        open={intro && !open}
        placement="left"
        arrow
        // Mismo nivel que el botón: el aviso de la primera vez no puede tapar
        // diálogos ni cajones (el Tooltip de MUI va por defecto a 1500 > modal 1300).
        PopperProps={{ "data-testid": "help-intro-tip", sx: { zIndex: LAUNCHER_Z_INDEX } }}
      >
        <ButtonBase
          data-testid="help-launcher"
          aria-label="Ayuda"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={openPanel}
          sx={{
            position: "fixed",
            right: { xs: 10, sm: 16 },
            bottom: { xs: "calc(84px + env(safe-area-inset-bottom))", sm: 16 },
            zIndex: LAUNCHER_Z_INDEX,
            width: { xs: 32, sm: 28 },
            height: { xs: 32, sm: 28 },
            borderRadius: "50%",
            fontSize: { xs: 17, sm: 15 },
            fontWeight: 700,
            color: "#fff",
            bgcolor: "rgba(15, 23, 42, 0.55)",
            boxShadow: "0 1px 4px rgba(0,0,0,.18)",
            opacity: intro ? 1 : 0.45,
            transition: "opacity .2s",
            "&:hover, &:focus-visible": { opacity: 1 },
            "&:focus-visible": { outline: "2px solid #E8590C", outlineOffset: 2 },
            "@media print": { display: "none" },
          }}
        >
          ?
        </ButtonBase>
      </Tooltip>
      {mounted && (
        <Suspense fallback={null}>
          <HelpPanel open={open} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
